import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { Checkbox } from './FieldComponents';
import Modal from './Modal';

const ArchivePasswordRetryModal = ({
  busy = false,
  error = '',
  fileName = '',
  onClose,
  onSubmit,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [error]);

  const clearPassword = () => {
    setPassword('');
    setShowPassword(false);
  };

  const close = () => {
    if (busy) return;
    clearPassword();
    onClose?.();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || password.length === 0) return;

    // Keep the plaintext out of parent/component state. Clear the rendered
    // input before awaiting the authenticated retry request and release the
    // short-lived local reference as soon as that request settles.
    let submittedPassword = password;
    setPassword('');
    try {
      await onSubmit?.(submittedPassword);
    } finally {
      submittedPassword = '';
    }
  };

  return (
    <Modal
      title="Enter ZIP password"
      instructions="Resume the document package already saved to this case."
      onClose={close}
      zIndex={1200}
    >
      <div className="modal-content-wrapper archive-password-retry-modal">
        <div className="archive-password-retry-summary">
          <KeyRound aria-hidden="true" size={20} />
          <div>
            <strong>{fileName || 'Document package'}</strong>
            <p>
              Re-enter the ZIP password to continue organizing the saved package.
              The original files do not need to be selected or uploaded again.
            </p>
          </div>
        </div>

        <form autoComplete="off" data-form-type="other" noValidate onSubmit={submit}>
          <div className="form-group full nm">
            <label className="subtext" htmlFor="archive-password-retry-input">ZIP password</label>
            <input
              aria-invalid={error ? 'true' : 'false'}
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect="off"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              data-lpignore="true"
              disabled={busy}
              id="archive-password-retry-input"
              maxLength={1024}
              name="archive_retry_decryption_key"
              onChange={(event) => setPassword(event.target.value)}
              ref={inputRef}
              spellCheck="false"
              type={showPassword ? 'text' : 'password'}
              value={password}
            />
          </div>

          <Checkbox
            ariaLabel="Show ZIP password"
            disabled={busy}
            label="Show password"
            mini
            onChange={() => setShowPassword((visible) => !visible)}
            value={showPassword}
          />

          {error && <div className="archive-password-retry-error" role="alert">{error}</div>}

          <div className="modal-footer-actions archive-password-retry-actions">
            <button className="action alt" disabled={busy} onClick={close} type="button">
              Cancel
            </button>
            <button className="action" disabled={busy || password.length === 0} type="submit">
              {busy ? <><Loader2 aria-hidden="true" className="spin" size={16} /> Resuming…</> : 'Resume package'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ArchivePasswordRetryModal;
